import { useState } from 'react';
import {
  Button,
  H1,
  H2,
  Input,
  Paragraph,
  Spinner,
  Text,
  XStack,
  YStack,
} from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { CalendarSubscription } from '@mrrmlab/shared-types';
import {
  useCalendarSubscriptions,
  useCreateCalendarSubscription,
  useDeleteCalendarSubscription,
  useSyncCalendarSubscription,
} from '../hooks';

function formatLastSync(iso: string | null): string {
  if (!iso) return 'noch nie';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CalendarSettingsScreen() {
  const list = useCalendarSubscriptions();
  const create = useCreateCalendarSubscription();
  const del = useDeleteCalendarSubscription();
  const sync = useSyncCalendarSubscription();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');

  const onAdd = () => {
    if (!name.trim() || !url.trim()) return;
    create.mutate(
      { name: name.trim(), url: url.trim() },
      {
        onSuccess: () => {
          setName('');
          setUrl('');
        },
      },
    );
  };

  return (
    <Screen scrollable>
      <YStack gap="$2">
        <H1>Kalender</H1>
        <Text fontSize="$3" opacity={0.7}>
          Verbinde ICS-Feeds, damit deine Termine im Tagesplan erscheinen.
        </Text>
      </YStack>

      <YStack
        gap="$3"
        padding="$4"
        borderRadius="$4"
        borderWidth={1}
        borderColor="$borderColor"
        backgroundColor="$color2"
      >
        <H2 fontSize="$6">Feed hinzufügen</H2>
        <YStack gap="$2">
          <Text fontSize="$2">Name</Text>
          <Input
            placeholder="z.B. Familie"
            value={name}
            onChangeText={setName}
            accessibilityLabel="Kalender-Name"
          />
        </YStack>
        <YStack gap="$2">
          <Text fontSize="$2">ICS-URL</Text>
          <Input
            placeholder="https://…/calendar.ics"
            value={url}
            onChangeText={setUrl}
            autoCapitalize="none"
            autoCorrect={false}
            accessibilityLabel="ICS-URL"
          />
        </YStack>
        {create.isError ? (
          <Text fontSize="$2" color="$red10">
            {create.error.message}
          </Text>
        ) : null}
        <Button
          theme="active"
          onPress={onAdd}
          disabled={create.isPending || !name.trim() || !url.trim()}
          accessibilityLabel="Kalender hinzufügen"
        >
          {create.isPending ? 'Speichere…' : 'Hinzufügen'}
        </Button>
      </YStack>

      <YStack gap="$2">
        <H2 fontSize="$6">Verbundene Feeds</H2>
        {list.isPending ? (
          <Spinner />
        ) : list.isError ? (
          <Paragraph color="$red10">Konnte Kalender nicht laden: {list.error.message}</Paragraph>
        ) : (list.data ?? []).length === 0 ? (
          <Paragraph opacity={0.7}>Noch kein Kalender verbunden.</Paragraph>
        ) : (
          <YStack gap="$2">
            {(list.data ?? []).map((sub) => (
              <SubscriptionRow
                key={sub.id}
                sub={sub}
                onSync={() => sync.mutate(sub.id)}
                onDelete={() => del.mutate(sub.id)}
                syncing={sync.isPending && sync.variables === sub.id}
                deleting={del.isPending && del.variables === sub.id}
              />
            ))}
          </YStack>
        )}
      </YStack>
    </Screen>
  );
}

function SubscriptionRow({
  sub,
  onSync,
  onDelete,
  syncing,
  deleting,
}: {
  sub: CalendarSubscription;
  onSync: () => void;
  onDelete: () => void;
  syncing: boolean;
  deleting: boolean;
}) {
  return (
    <YStack
      gap="$2"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
    >
      <YStack gap="$1">
        <Text fontSize="$5" fontWeight="600">{sub.name}</Text>
        <Text fontSize="$2" opacity={0.6} selectable>{sub.url}</Text>
        <Text fontSize="$1" opacity={0.5}>
          Letzter Sync: {formatLastSync(sub.lastSyncAt)}
        </Text>
      </YStack>
      <XStack gap="$2">
        <Button
          size="$3"
          onPress={onSync}
          disabled={syncing}
          accessibilityLabel={`Jetzt syncen: ${sub.name}`}
        >
          {syncing ? 'Syncing…' : 'Jetzt syncen'}
        </Button>
        <Button
          size="$3"
          chromeless
          color="$red10"
          onPress={onDelete}
          disabled={deleting}
          accessibilityLabel={`Entfernen: ${sub.name}`}
        >
          {deleting ? 'Lösche…' : 'Entfernen'}
        </Button>
      </XStack>
    </YStack>
  );
}
